import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Modal, Form, message, Typography, Popconfirm, Select, Tag } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin, resetPassword, updateUserChannel } from '../api/admin';
import { getDistinctChannels } from '../api/channel';
import { TimeUtil } from '../utils/timeUtil';
import { useAuthStore } from '../store/authStore';

const { Title } = Typography;
const { Password } = Input;

interface Admin {
  id: number;
  user: string;
  realName: string;
  email: string;
  mobileNum: string;
  role?: string;
  channelName?: string;
  createDate: string;
}

const AdminList: React.FC = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [distinctChannels, setDistinctChannels] = useState<string[]>(['client']);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isResetPasswordModalVisible, setIsResetPasswordModalVisible] = useState<boolean>(false);
  const [currentAdmin, setCurrentAdmin] = useState<Admin | null>(null);
  const [form] = Form.useForm();
  const [resetPasswordForm] = Form.useForm();
  const authStore = useAuthStore();

  useEffect(() => {
    fetchAdmins();
    loadChannels();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const { data } = await getAdmins();
      setAdmins(data);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      message.error('获取管理员列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadChannels = async () => {
    if (authStore.role !== 'admin') return;
    try {
      const { data } = await getDistinctChannels();
      setDistinctChannels(data);
    } catch (error) {
      console.error('获取频道列表失败:', error);
    }
  };

  const handleChannelChange = async (userId: number, newChannel: string) => {
    try {
      await updateUserChannel(userId, { channelName: newChannel });
      message.success('用户频道已更新');
      fetchAdmins();
    } catch (error) {
      console.error('更新频道失败:', error);
      message.error('更新频道失败');
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  const filteredAdmins = admins.filter(admin => {
    // 搜索过滤
    const matchesSearch = !searchText || 
      admin.user.toLowerCase().includes(searchText.toLowerCase()) ||
      admin.realName.toLowerCase().includes(searchText.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchText.toLowerCase());

    // 角色过滤
    const matchesRole = roleFilter === 'all' || admin.role === roleFilter;

    // 频道过滤
    const matchesChannel = channelFilter === 'all' || admin.channelName === channelFilter;

    return matchesSearch && matchesRole && matchesChannel;
  });

  const showModal = (admin?: Admin) => {
    form.resetFields();
    if (admin) {
      setCurrentAdmin(admin);
      form.setFieldsValue({
        user: admin.user,
        realName: admin.realName,
        email: admin.email,
        mobileNum: admin.mobileNum,
      });
    } else {
      setCurrentAdmin(null);
    }
    setIsModalVisible(true);
  };

  const showResetPasswordModal = (admin: Admin) => {
    resetPasswordForm.resetFields();
    setCurrentAdmin(admin);
    setIsResetPasswordModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleResetPasswordCancel = () => {
    setIsResetPasswordModalVisible(false);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (currentAdmin) {
        // 更新管理员
        await updateAdmin(currentAdmin.id, values);
        message.success('管理员更新成功');
      } else {
        // 创建管理员
        await createAdmin(values);
        message.success('管理员创建成功');
      }
      setIsModalVisible(false);
      fetchAdmins();
    } catch (error) {
      console.error('Failed to submit admin:', error);
      message.error(currentAdmin ? '更新管理员失败' : '创建管理员失败');
    }
  };

  const handleResetPassword = async (values: any) => {
    try {
      if (currentAdmin) {
        await resetPassword(currentAdmin.id, values.password);
        message.success('密码重置成功');
        setIsResetPasswordModalVisible(false);
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      message.error('密码重置失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteAdmin(id);
      message.success('管理员删除成功');
      fetchAdmins();
    } catch (error) {
      console.error('Failed to delete admin:', error);
      message.error('删除管理员失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: '姓名',
      dataIndex: 'realName',
      key: 'realName',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '手机号',
      dataIndex: 'mobileNum',
      key: 'mobileNum',
    },
    {
      title: '创建时间',
      dataIndex: 'createDate',
      key: 'createDate',
      render: (text: string) => TimeUtil.formatServerTime(text),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => {
        const colorMap: Record<string, string> = {
          admin: 'red',
          operator: 'orange',
          viewer: 'blue',
        };
        const labelMap: Record<string, string> = {
          admin: '管理员',
          operator: '运维员',
          viewer: '访客',
        };
        return <Tag color={colorMap[role] || 'default'}>{labelMap[role] || role}</Tag>;
      },
    },
    {
      title: '频道',
      dataIndex: 'channelName',
      key: 'channelName',
      width: 150,
      render: (text: string, record: Admin) => (
        authStore.role === 'admin' ? (
          <Select
            value={text || 'client'}
            onDropdownVisibleChange={(open) => { if (open) loadChannels(); }}
            onChange={(value) => handleChannelChange(record.id, value)}
            style={{ width: '100%' }}
            size="small"
          >
            {distinctChannels.map(channel => (
              <Select.Option key={channel} value={channel}>{channel}</Select.Option>
            ))}
          </Select>
        ) : (
          <Tag color="blue">{text || 'client'}</Tag>
        )
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Admin) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => showModal(record)}
          >
            编辑
          </Button>
          <Button 
            type="default" 
            icon={<LockOutlined />} 
            size="small"
            onClick={() => showResetPasswordModal(record)}
          >
            重置密码
          </Button>
          <Popconfirm
            title="确定要删除这个管理员吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>用户管理</Title>
        <Space>
          <Input
            placeholder="搜索用户名或姓名"
            prefix={<SearchOutlined />}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            value={roleFilter}
            onChange={setRoleFilter}
            style={{ width: 150 }}
          >
            <Select.Option value="all">全部角色</Select.Option>
            <Select.Option value="admin">管理员</Select.Option>
            <Select.Option value="operator">运维员</Select.Option>
            <Select.Option value="viewer">访客</Select.Option>
          </Select>
          <Select
            value={channelFilter}
            onDropdownVisibleChange={(open) => { if (open) loadChannels(); }}
            onChange={setChannelFilter}
            style={{ width: 150 }}
          >
            <Select.Option value="all">全部频道</Select.Option>
            {distinctChannels.map(channel => (
              <Select.Option key={channel} value={channel}>{channel}</Select.Option>
            ))}
          </Select>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => showModal()}
          >
            添加用户
          </Button>
        </Space>
      </div>
      
      <Table
        columns={columns}
        dataSource={filteredAdmins}
        rowKey="id"
        loading={loading}
      />
      
      {/* 添加/编辑管理员模态框 */}
      <Modal
        title={currentAdmin ? '编辑管理员' : '添加管理员'}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="user"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' }
            ]}
          >
            <Input placeholder="请输入用户名" disabled={!!currentAdmin} />
          </Form.Item>
          
          {!currentAdmin && (
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6个字符' }
              ]}
            >
              <Password placeholder="请输入密码" />
            </Form.Item>
          )}
          
          <Form.Item
            name="realName"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>
          
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>
          
          <Form.Item
            name="mobileNum"
            label="手机号"
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit">
              提交
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 重置密码模态框 */}
      <Modal
        title="重置密码"
        visible={isResetPasswordModalVisible}
        onCancel={handleResetPasswordCancel}
        footer={null}
      >
        <Form
          form={resetPasswordForm}
          layout="vertical"
          onFinish={handleResetPassword}
        >
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Password placeholder="请输入新密码" />
          </Form.Item>
          
          <Form.Item
            name="confirmPassword"
            label="确认密码"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Password placeholder="请确认密码" />
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit">
              提交
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminList;
